// src/pages/Accounting.js
//
// The Accounting hub (Supervisor Portal → Accounting tab, or /Accounting).
// Every accounting page is embedded here as a tab. They all read and write
// the same Firestore collections, so a number entered on one shows up on the
// others — the Directions tab explains where each number comes from.
//
// Admin only (it shows pay rates, bank lines and profit).

import React, { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";

import FinancialsDashboard from "./FinancialsDashboard";
import DailyLabor from "./DailyLabor";
import JobEstimateSpreadsheet from "./JobEstimateSpreadsheet";
import BankStatementEntries from "./BankStatementEntries";
import Deposits from "./Deposits";
import Expenses from "./Expenses";
import ProfitSharing from "./ProfitSharing";
import CashPlanDashboard from "./CashPlanDashboard";
import AccountingExports from "./AccountingExports";
import AccountingDirections from "./AccountingDirections";

export const ACCOUNTING_TABS = [
  { key: "overview", label: "Overview", Component: FinancialsDashboard },
  { key: "dailypay", label: "Tech Daily Pay", Component: DailyLabor },
  { key: "jobestimate", label: "Job Estimate Spreadsheet", Component: JobEstimateSpreadsheet },
  { key: "bank", label: "Bank Statement", Component: BankStatementEntries },
  { key: "deposits", label: "Deposits", Component: Deposits },
  { key: "expenses", label: "Expenses", Component: Expenses },
  { key: "profit", label: "Profit & Profit Sharing", Component: ProfitSharing },
  { key: "cashplan", label: "Cash Plan", Component: CashPlanDashboard },
  { key: "exports", label: "Accounting Exports", Component: AccountingExports },
  { key: "directions", label: "Directions", Component: null },
];

const TAB_STORAGE_KEY = "priority_accounting_tab";

const readStoredTab = () => {
  try {
    const stored = sessionStorage.getItem(TAB_STORAGE_KEY);
    return ACCOUNTING_TABS.some((t) => t.key === stored) ? stored : "overview";
  } catch {
    return "overview";
  }
};

const tabStyle = (active) => ({
  padding: "9px 14px",
  borderRadius: 999,
  border: active ? "1px solid #1d4ed8" : "1px solid #cbd5e1",
  background: active ? "#1d4ed8" : "#fff",
  color: active ? "#fff" : "#0f172a",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
  whiteSpace: "nowrap",
  width: "auto",
  margin: 0,
});

export default function Accounting() {
  const [activeTab, setActiveTab] = useState(readStoredTab);
  const [role, setRole] = useState(null);
  const [checking, setChecking] = useState(true);

  // Wait for Firebase to restore the signed-in user (a hard refresh of
  // /Accounting starts with currentUser = null).
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), async (user) => {
      if (!user) {
        setRole(null);
        setChecking(false);
        return;
      }
      try {
        const own = await getDoc(doc(db, "users", user.uid));
        if (own.exists()) {
          setRole(own.data().role || null);
        } else {
          const byAuth = await getDocs(query(collection(db, "users"), where("authUid", "==", user.uid)));
          setRole(byAuth.docs[0]?.data()?.role || null);
        }
      } catch (error) {
        console.error("Accounting: could not load role", error);
      } finally {
        setChecking(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const openTab = (key) => {
    setActiveTab(key);
    try {
      sessionStorage.setItem(TAB_STORAGE_KEY, key);
    } catch {
      /* ignore */
    }
  };

  if (checking) return <div style={{ padding: 20 }}>Loading…</div>;

  if (role !== "Admin") {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <h2>Accounting</h2>
        <p>Only Admin users can open the Accounting pages.</p>
      </div>
    );
  }

  const current = ACCOUNTING_TABS.find((t) => t.key === activeTab) || ACCOUNTING_TABS[0];
  const Current = current.Component;

  return (
    <div style={{ textAlign: "left" }}>
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 14,
          padding: "14px 16px",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Accounting</h2>
          <span style={{ color: "#64748b", fontSize: 14 }}>
            Priority Pest Defense — every page reads the same numbers.
          </span>
          <button
            type="button"
            onClick={() => openTab("directions")}
            style={{ ...tabStyle(false), marginLeft: "auto", borderColor: "#f59e0b", color: "#92400e", background: "#fffbeb" }}
          >
            ❓ How to use
          </button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {ACCOUNTING_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              style={tabStyle(tab.key === current.key)}
              onClick={() => openTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div key={current.key}>
        {current.key === "directions" ? (
          <AccountingDirections onOpenTab={openTab} />
        ) : (
          <Current onOpenTab={openTab} />
        )}
      </div>
    </div>
  );
}
